import { getConfig } from '../../configs/config';
import { getPartialMetaData } from '../../configs/meta';
import messages from '../../constants/logMessages.json';
import { AnalyticsPageView, SendQALErrorsToDynatrace } from '../../types/index';
import { jsonToString } from '../analyticsUtils';
import logging, { getMessage } from '../logging';
import { isEmpty } from './utilHelpers';

const {
  errorHeading,
  errorBold,
  infoDtHeading,
  infoRegular,
  warnHeading,
  warnBold,
} = messages;

interface DataLayerObject {
  event?: string;
  data?: Record<string, any>;
}

const isDtrumAvailable = () => {
  return (
    typeof window !== 'undefined' &&
    window.dtrum &&
    typeof window.dtrum.enableManualPageDetection === 'function'
  );
};

const dtrumQueue: (() => void)[] = [];
let dtrumReady = false;

export function onDtrumReady<T = void>(
  callback: () => T,
  fallback?: T
): T | void {
  const { mfeType, mfeContext } = getConfig()?.qalMfeConfig || {};

  // Disable firing of Dynatrace RUM methods in MFE if enableDynatraceMfe = false
  if (mfeType === 'mfe' && !mfeContext?.enableDynatraceMfe) return;

  if (isDtrumAvailable()) {
    dtrumReady = true;
    return callback();
  }

  dtrumQueue.push(() => callback());
  if (!dtrumReady) pollForDtrum();
  return fallback;
}

function pollForDtrum(maxAttempts = 40, intervalMs = 100): void {
  let attempts = 0;
  const interval = setInterval(() => {
    if (isDtrumAvailable()) {
      clearInterval(interval);
      dtrumReady = true;
      dtrumQueue.forEach((fn) => fn());
      dtrumQueue.length = 0;
    } else if (++attempts >= maxAttempts) {
      logging.error(
        getMessage('dtNotLoadedError', [
          String(intervalMs),
          String(maxAttempts),
        ]),
        errorHeading,
        infoRegular
      );
      clearInterval(interval);
      dtrumQueue.length = 0;
    }
  }, intervalMs);
}

// Transform qbeTrack.meta or dataLayer objects to Dynatrace Session Properties JSON object
// Keys must be lowercase and values must be shortString (i.e. not exceed 100 characters)
// Pass a second true parameter to mark all objects as public which means it's no PII
export const transformObjToDtSessionProperties = (
  inputObject: Record<string, any>,
  includePublic: boolean = false
): Record<string, { value: string; public?: boolean }> => {
  const MAX_LENGTH = 100;

  const truncateString = (str: string, maxLength: number): string =>
    str.length > maxLength ? str.slice(0, maxLength) : str;

  const transformedObject: Record<string, { value: string; public?: boolean }> =
    {};

  // console.log('includePublic', includePublic);

  Object.keys(inputObject).forEach((key) => {
    const value = inputObject[key];
    if (value !== null && value !== undefined) {
      transformedObject[key.toLowerCase()] = {
        value: truncateString(String(value), MAX_LENGTH),
        ...(includePublic ? { public: true } : {}),
      };
    }
  });

  // console.log('transformedObject', transformedObject);
  return transformedObject;
};

export const transformDataLayerToSessionProperties = (
  dlKeyWhitelist: Array<string>,
  dataLayerData: AnalyticsPageView
) => {
  // Return early if RUM not installed on page
  if (!isDtrumAvailable()) return [];

  // console.log('dlKeyWhitelist', dlKeyWhitelist);
  // console.log('dataLayerData', dataLayerData);

  // Create a new dataLayerData object which only has keys as defined in the dlKeyWhitelist array
  const filteredDataLayerData = dlKeyWhitelist.reduce(
    (acc: Record<string, any>, key: string) => {
      if (Object.prototype.hasOwnProperty.call(dataLayerData, key)) {
        acc[key] = dataLayerData[key];
      }
      return acc;
    },
    {}
  );

  // console.log('filteredDataLayerData', filteredDataLayerData);

  // Loop through the dataLayer to find the most recent 'page_view' event and take the data object
  let lastPageViewDataObj: Record<string, any> | null = null;
  for (let i = window.dataLayer.length - 1; i >= 0; i -= 1) {
    const dlObj = window.dataLayer[i] as DataLayerObject;
    if (dlObj && dlObj.event === 'page_view' && dlObj.data) {
      lastPageViewDataObj = dlObj.data;
      break;
    }
  }

  // Create a new object which only has the key values which have changed
  const onlyChangedData = Object.keys(filteredDataLayerData).reduce(
    (acc: Record<string, any>, key: string) => {
      if (
        !lastPageViewDataObj ||
        lastPageViewDataObj[key] !== filteredDataLayerData[key]
      ) {
        acc[key] = filteredDataLayerData[key];
      }
      return acc;
    },
    {}
  );

  // console.log('onlyChangedData', onlyChangedData);

  // Return early if contents of onlyChangedData is empty.
  if (isEmpty(onlyChangedData)) return [];

  // Create a new object which has been transformed by transformObjToDtSessionProperties
  const transformedData = transformObjToDtSessionProperties(onlyChangedData);
  // console.log('transformedData', transformedData);
  return transformedData;
};

export const getReturnMessage = (returnCode: number) => {
  switch (returnCode) {
    case 1:
      return 'New page tracked successfully';
    case 2:
      return 'New page started during onload.  It means it is cached and will be sent with load action.';
    case -1:
      return 'Page being set is the same as previous one';
    case -2:
      return 'Page is trying to be set but enableDynatraceManualPageDetection() was not run';
    default:
      return returnCode.toString();
  }
};

export const getLoggingMessage = (
  returnCode: number,
  returnMessage: string,
  setPagePayload: {
    name: string;
    group: string;
  }
) => {
  const returnMessageArr = [returnMessage, jsonToString(setPagePayload)];

  if (returnCode === 1) {
    logging.info(
      getMessage('trackDynatracePageInfo', returnMessageArr),
      infoDtHeading,
      infoRegular
    );
    return;
  }

  if (returnCode === -2) {
    logging.info(
      getMessage('trackDynatracePageError', returnMessageArr),
      errorHeading,
      errorBold
    );
    return;
  }

  logging.info(
    getMessage('trackDynatracePageWarn', returnMessageArr),
    warnHeading,
    warnBold
  );
};

export const handleDynatraceManualPageDetection = (): void => {
  // Return early if RUM not installed on page
  if (!isDtrumAvailable()) return;
  window.dtrum.enableManualPageDetection();
  logging.info(
    getMessage('dynatraceManualPageDetectionEnabled'),
    warnHeading,
    warnBold
  );
};

export const getDynatraceGroupString = (metaKeysForGroup: Array<string>) => {
  const metaObj = getPartialMetaData(metaKeysForGroup);
  return Object.values(metaObj)
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.toLowerCase().replace(/\s+/g, ''))
    .join('/');
};

type QALErrorKeys = keyof SendQALErrorsToDynatrace;

export const enableSendQALErrorsToDynatrace = (
  methodName: QALErrorKeys
): boolean => {
  const { sendQALErrorsToDynatrace } = getConfig();

  // Return true if sendQALErrorsToDynatrace is a boolean and true
  if (
    typeof sendQALErrorsToDynatrace === 'boolean' &&
    sendQALErrorsToDynatrace
  ) {
    return true;
  }

  // Return true if sendQALErrorsToDynatrace[methodName] is true
  if (
    sendQALErrorsToDynatrace &&
    typeof sendQALErrorsToDynatrace === 'object' &&
    !Array.isArray(sendQALErrorsToDynatrace) &&
    !isEmpty(sendQALErrorsToDynatrace) &&
    (sendQALErrorsToDynatrace as SendQALErrorsToDynatrace)[methodName] === true
  ) {
    return true;
  }

  return false;
};

export const loadRumScript = (scriptUrl: string) => {
  if (document.querySelector(`script[src="${scriptUrl}"]`)) {
    logging.info('Dynatrace script is already loaded.');
    return;
  }

  const script = document.createElement('script');
  script.src = scriptUrl;
  script.async = true;
  script.onload = () =>
    logging.info('QAL: RUM script inserted by QAL successfully.');
  script.onerror = () => logging.error('QAL: Failed to load Dynatrace script.');

  const firstScript = document.head.firstChild;
  return firstScript
    ? document.head.insertBefore(script, firstScript)
    : document.head.appendChild(script);
};
